import { Dropdown } from "primereact/dropdown"
import { InputText } from "primereact/inputtext"
import { InputNumber } from "primereact/inputnumber"
import { MultiSelect } from "primereact/multiselect"
import { errorMessages } from "../types/messages"
import { DateTime } from "luxon"
import {
  INTERVIEW_SECTIONS_INTAKE,
  INTERVIEW_SECTIONS_FOLLOWUP,
  INTERVIEW_SECTIONS_DISCHARGE,
  INTERVIEW_TYPES,
} from "../types/interviewTypes"
import { Calendar } from "primereact/calendar"

import styles from "../../../styles/Participant-GPRA.module.css"

/**
 * Helper function to format the value for a MultiSelect component.
 * It handles null/undefined values, arrays, and string values separated by backslashes.
 * @param {any} val - The value to format.
 * @returns {Array} - The formatted value as an array.
 */
const _setMultiselectValue = (val) => {
  // Return empty array if value is null or undefined
  if (!val) {
    return []
  } else if (Array.isArray(val)) {
    // Return value as is if it's already an array
    return val
  } else {
    // Split string by slash if present, otherwise wrap single value in array
    if (val?.includes("/")) {
      return getOptions(val)
    }
    return [val]
  }
}

/**
 * Helper function to sort organization questions based on their number and sub-question number.
 * @param {Object} a - First question object.
 * @param {Object} b - Second question object.
 * @returns {number} - Sort order (-1, 0, 1).
 */
const _sortOrganizations = (a, b) => {
  // Push items with number 0 to the end
  if (a.number === 0) {
    return 1
  }
  if (b.number === 0) {
    return 1
  }
  // Compare numbers
  if (a.number < b.number) {
    return -1
  }
  if (a.number > b.number) {
    return 1
  }
  // If numbers are equal, compare sub-question numbers
  if (a.number === b.number) {
    if (a.subQuestionNumber > b.subQuestionNumber) {
      return 1
    }
    if (a.subQuestionNumber < b.subQuestionNumber) {
      return -1
    }
  }
  return 0
}

/**
 * Helper function to format default and additional questions into a unified structure.
 * @param {Array} defaultQuestions - List of default questions.
 * @param {Array} additionalQuestions - List of additional questions.
 * @returns {Array} - Combined and sorted list of formatted questions.
 */
const _formatSectionQuestions = (defaultQuestions, additionalQuestions) => {
  let formatDefault = []
  let formatAdditional = []
  // Format default questions
  if (defaultQuestions) {
    formatDefault = defaultQuestions?.map((question) => {
      return {
        id: question.id,
        interviewType: question.interviewType,
        section: question.section,
        isEnterprise: question.isEnterprise,
        label: question.label,
        field: question.field,
        enabled: question.enabled,
        show: question.show,
        kind: "default",
      }
    })
  }
  // Format additional questions
  if (additionalQuestions) {
    formatAdditional = additionalQuestions?.map((question) => {
      let newValue = {}
      // Handle standard additional questions
      if (question?.additionalQuestion) {
        if (!question?.additionalQuestion?.questionGroup) {
          newValue = {
            number: question.orderNumber,
            enabled: question?.additionalQuestion?.enabled,
            required: question?.additionalQuestion?.required,
            interviewType: question?.additionalQuestion?.form,
            section: question?.additionalQuestion?.section,
            conditionalSource: question.additionalQuestion.conditionalSource,
            conditionalType: question.additionalQuestion.conditionalType,
            conditionalValue: question.additionalQuestion.conditionalValue,
            conditionalRequired:
              question.additionalQuestion.conditionalRequired,
            conditionalDisplay: question.additionalQuestion.conditionalDisplay,
            kind: "additional",
          }
        }
      }
      // Handle question groups
      if (question?.questionGroup) {
        if (!question?.questionGroup?.matrixGroup) {
          newValue = {
            number: question?.orderNumber,
            enabled: question?.questionGroup?.enabled,
            required: question?.questionGroup?.required,
            question: question?.questionGroup,
            interviewType: question?.questionGroup?.form,
            section: question?.questionGroup?.section,
            kind: "group",
          }
        }
      }
      // Handle matrix groups
      if (question?.matrixGroup) {
        newValue = {
          number: question?.orderNumber,
          enabled: question?.matrixGroup?.enabled,
          required: question?.matrixGroup?.required,
          question: question?.matrixGroup,
          interviewType: question?.matrixGroup?.form,
          section: question?.matrixGroup?.section,
          kind: "matrix",
        }
      }
      return newValue
    })
  }

  // Filter out any empty additional questions and merge with default questions
  const filterAdditionalEmpty = formatAdditional?.filter((a) => a.number)
  return [...formatDefault, ...filterAdditionalEmpty].sort(_sortOrganizations)
}

/**
 * Checks if there are any errors in the error object.
 * @param {Object} errors - The errors object.
 * @returns {boolean} - True if no errors exist, false otherwise.
 */
export const existsErrors = (errors) => {
  return Object.keys(errors).length === 0
}

/**
 * Displays a toast message (success or error).
 * @param {boolean} status - Status of the operation (true for success, false for error).
 * @param {string} message - The message to display.
 * @param {Object} toast - The toast reference.
 */
export const showMessage = (status, message, toast) => {
  if (toast.current) {
    if (!status) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: message,
        life: 3000,
      })
    } else {
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: message,
        life: 3000,
      })
    }
  }
}

/**
 * Determines if a specific question should be shown based on configuration.
 * @param {Array} questions - List of questions.
 * @param {string} field - The field identifier of the question.
 * @returns {boolean} - True if the question should be shown, false otherwise.
 */
export const showQuestion = (questions, field) => {
  return !!questions?.find((q) => q.field === field && q.show)
}

/**
 * Renders the appropriate input component based on the input type.
 * @param {string} inputType - Type of input (Dropdown, Multiselect, Text, Number, Date).
 * @param {any} value - Current value of the input.
 * @param {Function} onChange - Change handler function.
 * @param {string} key - Unique key for the input.
 * @param {Array} options - Options for Dropdown/Multiselect.
 * @param {boolean} error - Error state.
 * @param {string} questionId - ID of the question.
 * @param {Object} entity - The form entity data.
 * @param {Function} setEntity - Function to update entity data.
 * @param {Function} removeError - Function to remove error on change.
 * @param {boolean} disabled - Disabled state.
 * @param {number} alignment - Alignment type (1 for vertical, otherwise horizontal).
 * @param {boolean} defaultZero - Whether to default numeric values to zero.
 * @returns {JSX.Element} - The rendered input component.
 */
export const setInput = (
  inputType,
  value,
  onChange,
  key,
  options,
  error,
  questionId,
  entity,
  setEntity,
  removeError,
  disabled,
  alignment,
  defaultZero
) => {
  let className
  // Determine CSS class based on alignment and error state
  if (alignment === 1) {
    if (inputType === "Text") {
      className = error ? styles.inputText3Error : styles.inputText3
    } else {
      className = error ? styles.input3Error : styles.input3
    }
    if (inputType === "Date") {
      className = error ? styles.calendar : styles.calendarError
    }
  } else {
    className = error ? styles.horizontalElementError : styles.horizontalElement
  }

  // Render component based on input type
  switch (inputType) {
    case "Dropdown":
      return (
        <Dropdown
          className={className}
          value={value}
          onChange={(e) => {
            onChange(key, e.value, true, questionId, entity, setEntity)
            removeError(key)
          }}
          options={options}
          disabled={disabled}
          showClear={value}
          style={{ marginTop: "0rem" }}
        />
      )
    case "Multiselect":
      return (
        <MultiSelect
          className={className}
          value={_setMultiselectValue(value)}
          onChange={(e) => {
            onChange(key, e.value, true, questionId, entity, setEntity)
            removeError(key)
          }}
          options={options}
          showClear={value}
          disabled={disabled}
        />
      )
    case "Text":
      return (
        <InputText
          className={className}
          value={value}
          onChange={(e) => {
            onChange(key, e.target.value, true, questionId, entity, setEntity)
            removeError(key)
          }}
          disabled={disabled}
        />
      )
    case "Number":
      return (
        <InputNumber
          className={error ? styles.numberError : styles.number}
          value={!value && defaultZero ? 0 : value}
          onChange={(e) => {
            onChange(
              key,
              e.value.toString(),
              true,
              questionId,
              entity,
              setEntity
            )
            removeError(key)
          }}
          disabled={disabled}
          showClear={value}
          showButtons
          min={0}
        />
      )
    case "Date":
      const date = new Date(value)
      return (
        <Calendar
          className={className}
          value={date}
          onChange={(e) => {
            onChange(key, e.value, true, questionId, entity, setEntity)
            removeError(key)
          }}
          disabled={disabled}
          yearRange={`1900:${new Date().getFullYear()}`}
          yearNavigator
          monthNavigator
          showIcon
          showButtonBar
          style={{ paddingLeft: "0rem" }}
        />
      )
    default:
      break
  }
}

/**
 * Splits a string of options separated by slashes into an array.
 * @param {string} options - The slash-separated options string.
 * @returns {Array} - Array of options.
 */
export const getOptions = (options) => {
  return options.split("/")
}

/**
 * Extracts additional questions and their answers from the entity.
 * @param {Object} entity - The entity object containing form data.
 * @returns {Array} - List of additional questions with ids and values.
 */
export const getAdditionalQuestions = (entity) => {
  let additional = []
  // Iterate through entity entries to find 'additional' key
  Object.entries(entity).forEach(([key, value]) => {
    if (key === "additional") {
      value.forEach((val) => {
        // Handle array answers by converting to slash-separated string
        if (Array.isArray(val.answer)) {
          additional.push({
            id: val.additionalQuestionId,
            value: convertAdditionalQuestionsToText(val.answer),
          })
        } else {
          // Push single value answers
          additional.push({ id: val.additionalQuestionId, value: val.answer })
        }
      })
    }
  })
  return additional
}

/**
 * Updates the state of the form entity with new values.
 * @param {string} key - The key of the field being updated.
 * @param {any} value - The new value.
 * @param {boolean} additional - Flag indicating if this is an additional question.
 * @param {string} id - The ID of the expected additional question (if applicable).
 * @param {Object} entity - The current entity state.
 * @param {Function} setEntity - The state setter function.
 * @param {Array} extraValues - Optional extra values to update simultaneously.
 */
export const updateState = (
  key,
  value,
  additional,
  id,
  entity,
  setEntity,
  extraValues
) => {
  const newObject = { ...entity }
  // Handle additional questions
  if (additional) {
    const additionals = newObject["additional"]
    let newSave
    // If question already exists in additional array, update it
    if (additionals?.find((a) => a.additionalQuestionId === id)) {
      newSave = additionals?.map((s) => {
        let newValue = { ...s }
        if (s.additionalQuestionId === id) {
          newValue.answer = value
        }
        return newValue
      })
    } else {
      // If new additional question, add to array
      newSave = [
        ...additionals,
        ...[{ additionalQuestionId: id, answer: value, title: key }],
      ]
    }

    newObject["additional"] = newSave
  } else {
    // specific to default questions
    newObject[key] = value
  }
  // Update any extra values provided
  if (extraValues) {
    extraValues.forEach(({ key, value }) => {
      newObject[key] = value
    })
  }
  setEntity(newObject)
}

/**
 * Checks if a conditional question should be displayed based on the answer to its source question.
 * @param {Object} question - The question object containing conditional logic.
 * @param {Object} entity - The entity object containing current answers.
 * @returns {boolean} - True if the question should be displayed, false otherwise.
 */
export const checkDisplayConditional = (question, entity) => {
  let display = false
  if (question?.enabled) {
    if (question?.conditionalSource) {
      if (question.conditionalDisplay) {
        // Try to find answer in additional fields first
        let questionAnswer = entity["additional"]?.find(
          (v) => v.title === question.conditionalSource
        )?.answer
        // Fallback to top-level entity fields
        if (!questionAnswer) {
          questionAnswer = entity[question.conditionalSource]
        }
        // Fallback to searching by additionalQuestion title
        if (!questionAnswer) {
          questionAnswer = entity["additional"]?.find(
            (q) => q.additionalQuestion?.title === question.conditionalSource
          )?.answer
        }
        // If answer is an array, check if it includes the conditional value
        if (Array.isArray(questionAnswer)) {
          questionAnswer = questionAnswer.find(
            (q) => q === question.conditionalValue
          )
        }
        // Evaluate condition based on operator type
        switch (question.conditionalType) {
          case "==":
            if (questionAnswer === question.conditionalValue) {
              display = true
            }
            break
          case "=/":
            let answers = entity["additional"]?.find(
              (v) => v.title === question.conditionalSource
            )?.answer
            if (!answers && !questionAnswer) {
              answers = entity["additional"]?.find(
                (v) =>
                  v?.additionalQuestion?.title === question.conditionalSource
              )?.answer
            }
            if (!questionAnswer && answers && answers?.length > 0) {
              display = true
            }
            if (questionAnswer && !answers) {
              if (questionAnswer !== question.conditionalValue) {
                display = true
              }
            }
            if (!questionAnswer) {
              display = true
            }
            break
          case "<":
            if (questionAnswer < question.conditionalValue) {
              display = true
            }
            break
          case ">":
            if (questionAnswer > question.conditionalValue) {
              display = true
            }
            break
          default:
            break
        }
      } else {
        // If conditional display logic is not active but source present? (Logic unclear from original code but preserved)
        display = true
      }
    }
  }

  return display
}

/**
 * Determines if a field should be rendered based on its enabled state, required state, and conditional source.
 * @param {boolean} enabled - Whether the field is enabled.
 * @param {boolean} required - Whether the field is required.
 * @param {string} kind - The kind of field (e.g., "additional").
 * @param {string} conditionalSource - The source field for conditional logic.
 * @returns {boolean} - True if the field should be rendered.
 */
export const checkConditionalRender = (
  enabled,
  required,
  kind,
  conditionalSource
) => {
  if (kind === "additional") {
    if (enabled) {
      if (required) {
        return true
      } else {
        // If optional but has conditional source, hide it initially (implied logic)
        if (conditionalSource) {
          return false
        } else {
          return true
        }
      }
    }
    return false
  } else {
    return enabled
  }
}

/**
 * Validates the answers for a given section, checking required fields and conditional requirements.
 * @param {Object} entity - The entity containing form answers.
 * @param {string} section - The current section being validated.
 * @param {Array} additionalQuestions - List of additional questions.
 * @param {Object} errors - Current errors object to update.
 * @param {Array} defaultQuestions - List of default questions.
 * @returns {Object} - Updated errors object.
 */
export const validateQuestions = (
  entity,
  section,
  additionalQuestions,
  errors,
  defaultQuestions
) => {
  // Validate Additional Questions
  additionalQuestions
    ?.filter(
      (q) => q.additionalQuestion && q.additionalQuestion?.section === section
    )
    .forEach(({ additionalQuestion }) => {
      // Check if question should be validated (not a disabled group)
      if (
        !additionalQuestion.questionGroup ||
        additionalQuestion.questionGroup?.enabled
      ) {
        // Validation for required fields
        if (additionalQuestion.required && additionalQuestion.enabled) {
          if (
            !entity["additional"].find(
              (e) => e.additionalQuestionId === additionalQuestion.id
            )?.answer &&
            !(
              additionalQuestion.defaultZero &&
              additionalQuestion.type === "Number"
            )
          ) {
            errors[additionalQuestion.title] = errorMessages.requiredMessage
          }
        } else {
          // Validation for conditionally required fields
          if (
            additionalQuestion.conditionalSource &&
            additionalQuestion.conditionalRequired
          ) {
            // Get current question answer
            let questionAnswer = entity["additional"].find(
              (e) => e.title === additionalQuestion.title
            )?.answer
            // Get conditional source answer (check additional then main entity)
            let conditionalAnswer = entity["additional"].find(
              (e) => e.title === additionalQuestion.conditionalSource
            )?.answer
            if (!conditionalAnswer) {
              conditionalAnswer = entity[additionalQuestion.conditionalSource]
            }

            // Check condition logic
            // Determines if the conditional requirement is met based on the relationship between
            // the conditional source's answer and the required value/threshold.
            if (conditionalAnswer) {
              const shouldValidate = !(
                additionalQuestion.defaultZero &&
                additionalQuestion.type === "Number"
              )

              switch (additionalQuestion.conditionalType) {
                case "==":
                  // EQUALS: Source answer matches the value
                  if (
                    conditionalAnswer === additionalQuestion.conditionalValue
                  ) {
                    // Check if question is unanswered.
                    // If defaultZero is true for Number fields, empty is allowed (treated as 0).
                    if (!questionAnswer && shouldValidate) {
                      errors[additionalQuestion.title] =
                        errorMessages.requiredMessage
                    }
                  }
                  break
                case "=/":
                  // NOT EQUALS: Source answer does not match the value
                  if (
                    conditionalAnswer !== additionalQuestion.conditionalValue
                  ) {
                    if (!questionAnswer && shouldValidate) {
                      errors[additionalQuestion.title] =
                        errorMessages.requiredMessage
                    }
                  }
                  break
                case "<":
                  // LESS THAN: Source answer is less than the value
                  if (conditionalAnswer < additionalQuestion.conditionalValue) {
                    if (!questionAnswer && shouldValidate) {
                      errors[additionalQuestion.title] =
                        errorMessages.requiredMessage
                    }
                  }
                  break
                case ">":
                  // GREATER THAN: Source answer is greater than the value
                  if (conditionalAnswer > additionalQuestion.conditionalValue) {
                    if (!questionAnswer && shouldValidate) {
                      errors[additionalQuestion.title] =
                        errorMessages.requiredMessage
                    }
                  }
                  break
                default:
                  break
              }
            }
          }
        }
      }
    })
  
  // Validate Default Questions
  defaultQuestions
    .filter((d) => d.section === section)
    .forEach((question) => {
      // Validate conditional questions that are hidden by default (!question.show).
      // If the condition for the question is met (based on conditionalSource),
      // the field becomes active/visible and must be validated.
      if (!question.show) {
        if (question.conditionalSource && question.conditionalRequired) {
          if (!errors[question.field]) {
            // Check conditional source answer (check main entity then additional)
            let conditionalAnswer = entity[question.conditionalSource]
            let questionAnswer = entity[question.field]
            if (!conditionalAnswer) {
              conditionalAnswer = entity["additional"].find(
                (e) => e.title === question.conditionalSource
              )?.answer
            }
            
            // Check condition logic for default questions
            // Determines if the conditional requirement is met based on the relationship between
            // the conditional source's answer and the required value/threshold.
            if (conditionalAnswer) {
              const shouldValidate = !(
                question.defaultZero && question.type === "Number"
              )

              switch (question.conditionalType) {
                case "==":
                  // EQUALS: Source answer matches the value
                  if (conditionalAnswer === question.conditionalValue) {
                    // Check if question is unanswered.
                    // If defaultZero is true for Number fields, empty is allowed (treated as 0).
                    if (!questionAnswer && shouldValidate) {
                      errors[question.field] = errorMessages.requiredMessage
                    }
                  }
                  break
                case "=/":
                  // NOT EQUALS: Source answer does not match the value
                  if (conditionalAnswer !== question.conditionalValue) {
                    if (!questionAnswer && shouldValidate) {
                      errors[question.field] = errorMessages.requiredMessage
                    }
                  }
                  break
                case "<":
                  // LESS THAN: Source answer is less than the value
                  if (conditionalAnswer < question.conditionalValue) {
                    if (!questionAnswer && shouldValidate) {
                      errors[question.field] = errorMessages.requiredMessage
                    }
                  }
                  break
                case ">":
                  // GREATER THAN: Source answer is greater than the value
                  if (conditionalAnswer > question.conditionalValue) {
                    if (!questionAnswer && shouldValidate) {
                      errors[question.field] = errorMessages.requiredMessage
                    }
                  }
                  break
                default:
                  break
              }
            }
          }
        }
      }
    })
  return errors
}

/**
 * Formats an ISO date string to a local date string.
 * @param {string} date - The ISO date string.
 * @returns {string} - The formatted local date string.
 */
export const formatToLocalDate = (date) => {
  return DateTime.fromISO(date, {
    zone: "utc",
  })
    .toLocal()
    .toLocaleString()
}

/**
 * Splits options string into an array (same as getOptions).
 * @param {string} options - Slash-separated options string.
 * @returns {Array} - Array of options.
 */
export const convertAdditionalQuestionsOptions = (options) => {
  return options?.split("/")
}

/**
 * Joins an array of options into a slash-separated string.
 * @param {Array} options - Array of options to join.
 * @returns {string} - Slash-separated string.
 */
export const convertAdditionalQuestionsToText = (options) => {
  let stringOptions = ""
  for (let i = 0; i < options?.length; i++) {
    if (i !== options.length - 1) {
      stringOptions += options[i] + "/"
    } else {
      stringOptions += options[i]
    }
  }
  return stringOptions
}

/**
 * Formats default and additional questions, including mapping more detailed properties than _formatSectionQuestions.
 * @param {Array} defaultQuestions - List of default questions.
 * @param {Array} additionalQuestions - List of additional questions.
 * @returns {Array} - Combined and sorted list of formatted questions.
 */
export const formatMixedQuestions = (defaultQuestions, additionalQuestions) => {
  // Map default questions
  const formatDefault = defaultQuestions?.map((question) => {
    return {
      defaultZero: question.defaultZero,
      enterpriseCustomization: question.enterpriseCustomization,
      field: question.field,
      id: question.id,
      interviewType: question.interviewType,
      isEnterprise: question.isEnterprise,
      label: question.label,
      organizationId: question.organizationId,
      number: question.questionNumber,
      relatedTo: question.relatedTo,
      section: question.section,
      show: question.show,
      subQuestionNumber: question.subQuestionNumber,
      toggledOffBy: question.toggledOffBy,
      toggledOffDate: question.toggledOffDate,
      conditionalSource: question.conditionalSource,
      conditionalType: question.conditionalType,
      conditionalValue: question.conditionalValue,
      conditionalRequired: question.conditionalRequired,
      conditionalDisplay: question.conditionalDisplay,
      kind: "default",
    }
  })
  // Map additional questions
  const formatAdditional = additionalQuestions?.map((question) => {
    let newValue = {}
    if (question?.additionalQuestion) {
      if (!question?.additionalQuestion?.questionGroup) {
        newValue = {
          defaultZero: question?.additionalQuestion?.defaultZero,
          number: question.orderNumber,
          enabled: question?.additionalQuestion?.enabled,
          required: question?.additionalQuestion?.required,
          question: question?.additionalQuestion,
          conditionalSource: question.additionalQuestion.conditionalSource,
          conditionalType: question.additionalQuestion.conditionalType,
          conditionalValue: question.additionalQuestion.conditionalValue,
          conditionalRequired: question.additionalQuestion.conditionalRequired,
          conditionalDisplay: question.additionalQuestion.conditionalDisplay,
          kind: "additional",
        }
      }
    }
    // Handle question groups and matrix groups (similar to _formatSectionQuestions but with less nesting in result?)
    if (question?.questionGroup) {
      if (!question?.questionGroup?.matrixGroup) {
        newValue = {
          number: question?.orderNumber,
          enabled: question?.questionGroup?.enabled,
          required: question?.questionGroup?.required,
          question: question?.questionGroup,
          kind: "group",
        }
      }
    }
    if (question?.matrixGroup) {
      newValue = {
        number: question?.orderNumber,
        enabled: question?.matrixGroup?.enabled,
        required: question?.matrixGroup?.required,
        question: question?.matrixGroup,
        kind: "matrix",
      }
    }
    return newValue
  })
  
  // Filter and sort
  const filterAdditionalEmpty = formatAdditional.filter((a) => a.number)
  return [...formatDefault, ...filterAdditionalEmpty].sort(_sortOrganizations)
}

/**
 * Checks if a section is enabled based on mixed questions.
 * @param {string} field - The field to check.
 * @param {Array} mixedQuestions - The list of questions.
 * @returns {boolean} - True if enabled.
 */
export const sectionEnabled = (field, mixedQuestions) => {
  return !!mixedQuestions?.find((q) => q.field === field)
}

/**
 * Determines which sections are available for a given interview type.
 * @param {string} interviewType - The type of interview (Intake, FollowUp, Discharge).
 * @param {Array} defaultQuestions - Default questions list.
 * @param {Array} additionalQuestions - Additional questions list.
 * @param {boolean} administrativeInterview - Flag for administrative interview.
 * @returns {Array} - Sorted list of available sections.
 */
export const availableSections = (
  interviewType,
  defaultQuestions,
  additionalQuestions,
  administrativeInterview
) => {
  // Format questions first
  const mixedQuestions = _formatSectionQuestions(
    defaultQuestions,
    additionalQuestions
  ).filter((question) => question.interviewType === interviewType)

  let availableSections = []
  
  // Logic for INTAKE sections
  if (interviewType === INTERVIEW_TYPES.INTAKE) {
    INTERVIEW_SECTIONS_INTAKE.forEach((section) => {
      const filteredQuestions = mixedQuestions.filter(
        (question) =>
          question.section === section.title &&
          section.fields.includes(question.field)
      )
      if (filteredQuestions.some((q) => q?.enabled || q?.show)) {
        availableSections.push({
          section:
            section.title === "recordManagement"
              ? section.alias
              : section.title,
          index: section.number,
        })
      }
    })
    
    // Add custom sections not in default map?
    mixedQuestions
      .filter((f) => f.kind !== "default" && f.enabled)
      .forEach((q) => {
        if (q.section !== "recordManagement") {
          if (!availableSections.find((a) => a.section === q.section)) {
            availableSections.push({
              section: q.section,
              index: INTERVIEW_SECTIONS_INTAKE.find(
                (s) => s.title === q.section
              )?.number,
            })
          }
        }
      })
  } else if (interviewType === INTERVIEW_TYPES.FOLLOW_UP) {
    // Logic for FOLLOW_UP sections
    INTERVIEW_SECTIONS_FOLLOWUP.forEach((section) => {
      const filteredQuestions = mixedQuestions.filter(
        (question) =>
          question.section === section.title &&
          section.fields.includes(question.field)
      )
      if (filteredQuestions.some((q) => q?.enabled || q?.show)) {
        availableSections.push({
          section:
            section.title === "startInterview" ? section.alias : section.title,
          index: section.number,
        })
      }
    })
    
    // Add additional sections
    mixedQuestions
      .filter((f) => f.kind !== "default" && f.enabled)
      .forEach((q) => {
        if (q.section !== "recordManagement") {
          const section = q.section === "startInterview" ? "start" : q.section
          if (!availableSections.find((a) => a.section === section)) {
            availableSections.push({
              section: section,
              index: INTERVIEW_SECTIONS_FOLLOWUP.find(
                (s) => s.title === q.section
              )?.number,
            })
          }
        }
      })
      
    // Handle administrative interview flags
    if (!administrativeInterview) {
      availableSections = availableSections.map((s) => {
        let newValue = { ...s }
        if (
          s.section === "start" ||
          s.section === "followUp" ||
          s.section === "summary"
        ) {
          newValue.administrativeInterview = administrativeInterview
        }
        return newValue
      })
    }
  } else if (interviewType === INTERVIEW_TYPES.DISCHARGE) {
    // Logic for DISCHARGE sections
    INTERVIEW_SECTIONS_DISCHARGE.forEach((section) => {
      const filteredQuestions = mixedQuestions.filter(
        (question) =>
          question.section === section.title &&
          section.fields.includes(question.field)
      )
      if (filteredQuestions.some((q) => q?.enabled || q?.show)) {
        availableSections.push({
          section:
            section.title === "startInterview" ? section.alias : section.title,
          index: section.number,
        })
      }
    })
    
    // Add additional sections
    mixedQuestions
      .filter((f) => f.kind !== "default" && f.enabled)
      .forEach((q) => {
        if (q.section !== "recordManagement") {
          const section = q.section === "startInterview" ? "start" : q.section
          if (!availableSections.find((a) => a.section === section)) {
            availableSections.push({
              section: section,
              index: INTERVIEW_SECTIONS_DISCHARGE.find(
                (s) => s.title === q.section
              )?.number,
            })
          }
        }
      })

    // Handle administrative interview flags
    if (!administrativeInterview) {
      availableSections = availableSections.map((s) => {
        let newValue = { ...s }
        if (
          s.section === "start" ||
          s.section === "discharge" ||
          s.section === "summary"
        ) {
          newValue.administrativeInterview = administrativeInterview
        }
        return newValue
      })
    }
  }
  // Always add summary section
  availableSections.push({
    section: "summary",
    index: 20,
    administrativeInterview: administrativeInterview,
  })
  
  // Sort by index
  return availableSections.sort((a, b) => {
    return a.index - b.index
  })
}

/**
 * Determines the next form section to navigate to.
 * @param {Array} sections - Available sections.
 * @param {number} index - Current section index.
 * @param {boolean} administrativeInterview - Administrative interview flag.
 * @returns {string} - The section identifier for the next form.
 */
export const nextForm = (sections, index, administrativeInterview) => {
  if (!administrativeInterview) {
    const filteredSections = sections?.filter(
      (s) => s?.administrativeInterview === false
    )
    return filteredSections[index + 1]?.section
  }
  return sections[index + 1]?.section
}

/**
 * Determines the previous form section to navigate to.
 * @param {Array} sections - Available sections.
 * @param {number} index - Current section index.
 * @param {boolean} administrativeInterview - Administrative interview flag.
 * @returns {string} - The section identifier for the previous form.
 */
export const prevForm = (sections, index, administrativeInterview) => {
  if (administrativeInterview === false) {
    const filteredSections = sections?.filter(
      (s) => s?.administrativeInterview === false
    )
    return filteredSections[index - 1]?.section
  }
  return sections[index - 1]?.section
}

/**
 * Filters additional questions for a specific section.
 * @param {Array} additionalQuestions - List of additional questions.
 * @param {string} section - The section to filter by.
 * @returns {Array} - Filtered list of questions.
 */
export const filterAdditionalQuestions = (additionalQuestions, section) => {
  // eslint-disable-next-line array-callback-return
  return additionalQuestions?.filter((q) => {
    if (q?.additionalQuestion) {
      if (q?.additionalQuestion?.section === section) {
        return q
      }
    }
    if (q?.matrixGroup) {
      if (q?.matrixGroup?.section === section) {
        return q
      }
    }
    if (q?.questionGroup) {
      if (q?.questionGroup?.section === section) {
        return q
      }
    }
  })
}

/**
 * Checks if the current user is authorized to edit the form.
 * @param {number} staff_role - Role ID of the staff.
 * @param {string} creator_staff_id - ID of the staff who created the record.
 * @param {string} current_staff_id - ID of the current staff.
 * @returns {boolean} - True if authorized.
 */
export const editAuthorized = (
  staff_role,
  creator_staff_id,
  current_staff_id
) => {
  let authorized = false
  // Creator can always edit
  if (creator_staff_id === current_staff_id) {
    authorized = true
  } else {
    // Specific roles can edit (2, 3, 4)
    authorized = staff_role === 2 || staff_role === 3 || staff_role === 4
  }
  return authorized
}
